import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import printing from '../services/printing';
import { FaPrint } from 'react-icons/fa';

const SaleBatchDetailsModal = ({ batch, show, onHide }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refundMode, setRefundMode] = useState(false);
  const [refunds, setRefunds] = useState({}); // { [saleId]: { qty, price } }
  const [existingRefundsBySale, setExistingRefundsBySale] = useState({}); // { [saleId]: [refundRows] }
  const [refundsLoading, setRefundsLoading] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);

  const { token, user } = useAuth();

  useEffect(() => {
    if (!batch?.SellBatchID) return;
    const fetchItems = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`/api/sale-history/by-sellbatch/${batch.SellBatchID}`, {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            'x-owner-id': user?.ownerId || user?.id || '',
            'x-staff-id': user?.staffId || ''
          }
        });
        if (!res.ok) throw new Error('Server error');
        const data = await res.json();
        const arr = Array.isArray(data) ? data : [];
        setItems(arr);
        // Initialize default refunds mapping when data arrives
        const def = {};
        for (const it of arr) {
          def[it.SaleID] = {
            qty: Number(it.QuantitySold) || 0,
            price: Number(it.SalePricePerUnit) || 0
          };
        }
        setRefunds(def);
      } catch (err) {
        setError(err.message);
        setItems([]);
      } finally {
        setLoading(false);
      }
    };
    fetchItems();
  }, [batch, token, user]);

  // After items are fetched, load existing refunds for refunded sales
  useEffect(() => {
    const run = async () => {
      try {
        setExistingRefundsBySale({});
        if (!Array.isArray(items) || items.length === 0) return;
        const saleIds = items.filter(it => !!it.IsRefund).map(it => it.SaleID);
        if (saleIds.length === 0) return;
        setRefundsLoading(true);
        const params = new URLSearchParams();
        params.set('saleIds', saleIds.join(','));
        params.set('limit', '1000');
        const res = await fetch(`/api/refunds?${params.toString()}`, {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            'x-owner-id': user?.ownerId || user?.id || '',
            'x-staff-id': user?.staffId || ''
          }
        });
        if (!res.ok) throw new Error('Refundlarni yuklashda xatolik');
        const json = await res.json();
        const rows = Array.isArray(json?.items) ? json.items : [];
        const map = {};
        for (const r of rows) {
          const sid = r.sale_id;
          if (!map[sid]) map[sid] = [];
          map[sid].push(r);
        }
        setExistingRefundsBySale(map);
      } catch (e) {
        // ignore; show nothing
      } finally {
        setRefundsLoading(false);
      }
    };
    run();
  }, [items, token, user]);

  const formatCurrency = (amount, currency) => {
    if (amount == null || currency == null) return 'N/A';
    return `${Number(amount).toLocaleString()} ${currency}`;
  };

  const onChangeRefundQty = (saleId, value, item) => {
    const maxQty = Number(item?.QuantitySold) || 0;
    const costing = (item?.costing_method || '').toUpperCase();
    let qty = value;
    // Normalize input according to costing method
    if (costing === 'SPECIFIC') {
      // integers only
      qty = qty.replace(/[^0-9]/g, '');
      qty = qty ? String(Math.min(parseInt(qty, 10) || 0, Math.floor(maxQty))) : '';
    } else {
      // FIFO: up to 3 decimals
      qty = qty.replace(/[^0-9.,]/g, '').replace(',', '.');
      const parts = qty.split('.');
      if (parts.length > 1) parts[1] = parts[1].slice(0, 3);
      qty = parts.join('.');
      const asNum = Number(qty);
      if (!isNaN(asNum)) {
        qty = String(Math.min(asNum, maxQty));
      }
    }
    setRefunds(prev => ({
      ...prev,
      [saleId]: {
        qty: qty === '' ? '' : Number(qty),
        price: (prev[saleId]?.price ?? Number(item?.SalePricePerUnit)) || 0
      }
    }));
  };

  const onChangeRefundPrice = (saleId, value) => {
    let v = value.replace(/[^0-9.,]/g, '').replace(',', '.');
    const parts = v.split('.');
    if (parts.length > 1) parts[1] = parts[1].slice(0, 3);
    v = parts.join('.');
    setRefunds(prev => ({
      ...prev,
      [saleId]: { qty: prev[saleId]?.qty ?? 0, price: v === '' ? '' : Number(v) }
    }));
  };


  const validation = useMemo(() => {
    if (!refundMode) return { valid: true, errors: {}, hasAny: false };
    const errors = {};
    let hasAny = false;
    for (const it of items) {
      const sId = it.SaleID;
      const ref = refunds[sId] || { qty: 0, price: 0 };
      const qtyNum = Number(ref.qty);
      const priceNum = Number(ref.price);
      const maxQty = Number(it.QuantitySold) || 0;
      const costing = (it.costing_method || '').toUpperCase();

      if (qtyNum > 0) {
        hasAny = true;
        let ok = true;
        if (qtyNum > maxQty) ok = false;
        if (!priceNum || priceNum <= 0) ok = false;
        if (costing === 'SPECIFIC' && !Number.isInteger(qtyNum)) ok = false;
        if (!ok) errors[sId] = true;
      }
    }
    return { valid: hasAny && Object.keys(errors).length === 0, errors, hasAny };
  }, [refundMode, items, refunds]);

  const [isSubmittingRefund, setIsSubmittingRefund] = useState(false);

  const handleSubmitRefund = async () => {
    if (!refundMode) return;
    if (!validation.valid) {
      alert('Qaytarish qiymatlarini tekshiring.');
      return;
    }
    setIsSubmittingRefund(true);
    try {
      const activeItems = items
        .map(it => ({
          saleId: it.SaleID,
          refundQuantity: Number(refunds[it.SaleID]?.qty || 0),
          refundPrice: Number(refunds[it.SaleID]?.price || 0)
        }))
        .filter(x => x.refundQuantity > 0);
      const payload = {
        sellBatchID: batch?.SellBatchID,
        items: activeItems
      };
      const res = await fetch('/api/refunds', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'x-owner-id': user?.ownerId || user?.id || '',
          'x-staff-id': user?.staffId || ''
        },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.error || 'Refund yuborishda xatolik');
      }
      // Refresh items
      setRefundMode(false);
      onHide?.();
    } catch (e) {
      alert(e.message || 'Xatolik');
    } finally {
      setIsSubmittingRefund(false);
    }
  };

  const handlePrintBatch = async () => {
    if (!Array.isArray(items) || items.length === 0) {
      toast.error('Chop etiladigan mahsulotlar topilmadi');
      return;
    }
    setPrintLoading(true);
    try {
      // Compose cart-like array for printService
      const cart = items.map((it) => ({
        name: it.product_name,
        qty: Number(it.QuantitySold) || 0,
        price: Number(it.SalePricePerUnit) || 0,
        sum: (Number(it.QuantitySold) || 0) * (Number(it.SalePricePerUnit) || 0),
      }));
      const deliveryPrice = Number(items[0]?.DeliveryPrice || 0);
      const currency = batch?.Currency || items[0]?.Currency || '';
      const totalProducts = cart.reduce((s, it) => s + Number(it.sum), 0);
      const overall = totalProducts + (deliveryPrice > 0 ? deliveryPrice : 0);
      // Compose payload expected by /print
      await printing.printSaleReceipt({
        business: {}, // No business meta
        sale: {
          batchId: batch?.SellBatchID,
          date: batch?.SaleDate || items[0]?.SaleDate
        },
        cart,
        payments: [], // Not available here
        deliveryPrice,
        totals: {
          totalProducts,
          deliveryPrice,
          overall
        },
        options: { currency }
      });
      toast.success('Chek printerga yuborildi!');
    } catch (e) {
      toast.error(`Chek yuborilmadi: ${e?.message || e}`);
    } finally {
      setPrintLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header className="d-flex align-items-center justify-content-between">
        <Modal.Title className="flex-grow-1">Partiya Tafsilotlari:
          <small className='fs-6 text-secondary ms-1'>
            (ID: {batch.SellBatchID})
          </small>
        </Modal.Title>

        <div className="d-flex align-items-center gap-2">
          <div className="form-check form-switch d-flex align-items-center">
            <input
              className="form-check-input"
              type="checkbox"
              id="refundSwitch"
              checked={refundMode}
              onChange={(e) => setRefundMode(e.target.checked)}
            />
            <label className="form-check-label text-danger fw-bold ms-1" htmlFor="refundSwitch">
              Qaytarish
            </label>
          </div>
          { /* --- PRINT BUTTON --- */}
          {!loading && !error && items.length > 0 && !refundMode && (
            <Button
              className='me-2 ms-4'
              variant="success"
              size="sm"
              disabled={printLoading}
              onClick={handlePrintBatch}
            >
              {printLoading ? 'Chop etilmoqda...' : <span><FaPrint /> Chek</span>}
            </Button>
          )}
        </div>
        <button
          type="button"
          className="btn-close"
          aria-label="Close"
          onClick={onHide}
        ></button>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center py-6">Yuklanmoqda...</div>
        ) : error ? (
          <div className="text-center text-danger py-6">Xatolik: {error}</div>
        ) : items.length === 0 ? (
          <div className="text-center py-6">Hech qanday mahsulot topilmadi</div>
        ) : (
          <div className="table-responsive">
            <table className="table table-bordered">
              <thead className="table-light">
                <tr>
                  <th>Mahsulot</th>
                  <th>Soni</th>
                  <th>Narxi</th>
                  <th>Jami</th>
                  <th>Valyuta</th>
                  <th>Shtrix-kod</th>
                  <th>Sana</th>
                  {refundMode && (
                    <>
                      <th>Qaytarish miqdori</th>
                      <th>Qaytarish narxi</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => (
                  <React.Fragment key={item.SaleID || idx}>
                    <tr>
                      <td>{item.product_name || '-'}</td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm">{item.QuantitySold}</td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm">{Number(item.SalePricePerUnit).toLocaleString()}</td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm">{((Number(item.QuantitySold) || 0) * (Number(item.SalePricePerUnit) || 0)).toLocaleString()}</td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm">{item.Currency}</td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm">{item.barcode || '-'}</td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm">{item.SaleDate ? new Date(item.SaleDate).toLocaleDateString('uz-UZ') : '-'}</td>
                      {refundMode && (
                        <>
                          <td style={{ minWidth: 140 }}>
                            <input
                              type="text"
                              className={`form-control ${validation.errors[item.SaleID] ? 'is-invalid' : ''}`}
                              value={(refunds[item.SaleID]?.qty ?? '').toString()}
                              onChange={(e) => onChangeRefundQty(item.SaleID, e.target.value, item)}
                              placeholder="Miqdor"
                            />
                            <div className="form-text">Max: {item.QuantitySold}{(item.costing_method || '').toUpperCase() === 'SPECIFIC' ? '' : ' (3 raqam)'}
                            </div>
                          </td>
                          <td style={{ minWidth: 140 }}>
                            <input
                              type="text"
                              className={`form-control ${validation.errors[item.SaleID] ? 'is-invalid' : ''}`}
                              value={(refunds[item.SaleID]?.price ?? '').toString()}
                              onChange={(e) => onChangeRefundPrice(item.SaleID, e.target.value)}
                              placeholder="Narx"
                            />
                          </td>
                        </>
                      )}
                    </tr>
                    {/* Existing refunds shown below the related sale row */}
                    {Array.isArray(existingRefundsBySale[item.SaleID]) && existingRefundsBySale[item.SaleID].map(r => (
                      <tr className="table-success" key={`ref-${item.SaleID}-${r.id}`}>
                        <td>{r.product_name || '-'}</td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm">{r.refund_quantity}</td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm">{Number(r.refund_price).toLocaleString()}</td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm">{Number((r.refund_quantity || 0) * (r.refund_price || 0)).toLocaleString()}</td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm">{r.currency}</td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm"><span className="badge bg-success">Refund</span></td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm">{r.created_at ? new Date(r.created_at).toLocaleDateString('uz-UZ') : '-'}</td>
                        {refundMode && (<><td></td><td></td></>)}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            {/* Totals section */}
            <div className="mt-3">
              {(() => {
                const currency = batch?.Currency || (items[0]?.Currency) || '';
                const totalSale = items.reduce((sum, it) => sum + (Number(it.QuantitySold) || 0) * (Number(it.SalePricePerUnit) || 0), 0);
                const deliveryPrice = Number(items[0]?.DeliveryPrice || 0);
                const grand = totalSale + deliveryPrice;
                return (
                  <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                    <span className="badge bg-light text-dark p-2">Umumiy savdo narxi: <strong>{formatCurrency(totalSale, currency)}</strong></span>
                    <span className="badge bg-light text-dark p-2">Yetkazib berish narxi: <strong>{formatCurrency(deliveryPrice, currency)}</strong></span>
                    <span className="badge bg-primary p-2">Jami: <strong>{formatCurrency(grand, currency)}</strong></span>
                  </div>
                );
              })()}
            </div>
          </div>
        )}
      </Modal.Body>
      {refundMode && (
        <div className="modal-footer flex-column">
          <div
            className="w-100 p-3 mb-3 text-center rounded-3"
            style={{
              backgroundColor: '#fff5f5',
              border: '1px solid #f5c2c7',
              color: '#842029',
              fontSize: '0.95rem'
            }}
          >
            Mahsulotni qaytarishda pulni ham qaytarish uchun hisobotlar sahifasida <strong>Chiqim</strong> bo'limiga o'ting
          </div>
          <div className="d-flex justify-content-end w-100 gap-2">
            <Button variant="secondary" onClick={onHide} disabled={isSubmittingRefund}>Bekor qilish</Button>
            <Button variant="primary" onClick={handleSubmitRefund} disabled={!validation.valid || isSubmittingRefund}>
              {isSubmittingRefund ? 'Yuborilmoqda...' : 'Tasdiqlash'}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default SaleBatchDetailsModal;
